import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';

/* ─── helpers ─── */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SEM_LABELS = {
  SEM_1: '1st Semester', SEM_2: '2nd Semester', SEM_3: '3rd Semester',
  SEM_4: '4th Semester', SEM_5: '5th Semester', SEM_6: '6th Semester',
  SEM_7: '7th Semester', SEM_8: '8th Semester'
};

const DEPARTMENTS = ['CS', 'CD', 'CI', 'CY', 'EC', 'ME'];
const SEMESTERS   = Object.keys(SEM_LABELS);
const SECTIONS    = ['A', 'B', 'C'];

/* ─── CSV builder ─── */
function rowsToCsv(rows, meta) {
  const header = [
    'USN', 'Student Name', 'Email', 'Department', 'Semester',
    'Subject Code', 'Subject Name',
    'Total Classes', 'Present', 'Absent', 'Late', 'Excused',
    'Attendance %'
  ];
  const escape = (v) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    `# Attendance Export — ${meta.department} | ${SEM_LABELS[meta.semester] || meta.semester}${meta.section ? ' | Section ' + meta.section : ''} | ${meta.startDate} to ${meta.endDate}`,
    `# Generated: ${new Date().toLocaleString()}`,
    '',
    header.map(escape).join(','),
    ...rows.map(r => [
      r.usn, r.name, r.email, r.department, SEM_LABELS[r.semester] || r.semester,
      r.subjectCode, r.subjectName,
      r.totalClasses, r.present, r.absent, r.late, r.excused,
      r.attendancePercent + '%'
    ].map(escape).join(','))
  ];
  return lines.join('\n');
}

/* ─── save CSV ─── */
async function saveCsv(csvString, suggestedName) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: [{ description: 'CSV File', accept: { 'text/csv': ['.csv'] } }]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false; // user cancelled
      // fallback to anchor
    }
  }
  // Fallback
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
  return true;
}

/* ─── 1-year range guard ─── */
const MAX_RANGE_DAYS = 365;
const diffDays = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

/* ─── status badge ─── */
const badgeColor = (pct) => {
  const v = parseFloat(pct);
  if (v >= 75) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
  if (v >= 60) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-red-100 text-red-700 border-red-300';
};

/* ══════════════════════════════════════════════════════════════ */
const ExportAttendance = () => {
  const navigate = useNavigate();
  const t = today();

  /* form */
  const [startDate,   setStartDate]   = useState(t);
  const [endDate,     setEndDate]     = useState(t);
  const [department,  setDepartment]  = useState('');
  const [semester,    setSemester]    = useState('');
  const [section,     setSection]     = useState('');
  const [subjectId,   setSubjectId]   = useState('');

  /* data */
  const [subjects,    setSubjects]    = useState([]);
  const [loadingSub,  setLoadingSub]  = useState(false);
  const [rows,        setRows]        = useState(null);   // null = not fetched yet
  const [meta,        setMeta]        = useState(null);
  const [loading,     setLoading]     = useState(false);

  /* ── derived: is date range within 1 year? ── */
  const rangeDays = startDate && endDate ? diffDays(startDate, endDate) : 0;
  const dateRangeExceeded = rangeDays > MAX_RANGE_DAYS;

  /* fetch subjects whenever dept + sem change */
  useEffect(() => {
    if (!department || !semester) { setSubjects([]); setSubjectId(''); return; }
    setLoadingSub(true);
    api.get(`/attendance/subjects?department=${department}&semester=${semester}`)
      .then(r => setSubjects(r.data.data || []))
      .catch(() => { toast.error('Failed to load subjects'); setSubjects([]); })
      .finally(() => setLoadingSub(false));
  }, [department, semester]);

  /* ── fetch preview data ── */
  const handleFetch = useCallback(async () => {
    if (!startDate || !endDate || !department || !semester) {
      toast.error('Please fill in Date Range, Department and Semester');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before or equal to end date');
      return;
    }
    if (diffDays(startDate, endDate) > MAX_RANGE_DAYS) {
      toast.error('Date range must not exceed 1 year (365 days)');
      return;
    }
    try {
      setLoading(true);
      setRows(null);
      const params = new URLSearchParams({ startDate, endDate, department, semester });
      if (section)   params.append('section',   section);
      if (subjectId) params.append('subjectId', subjectId);

      const res = await api.get(`/attendance/export?${params.toString()}`);
      setRows(res.data.data   || []);
      setMeta(res.data.meta   || null);
      if ((res.data.data || []).length === 0) {
        toast.warn('No marked attendance records found for these filters');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, department, semester, section, subjectId]);

  /* ── download CSV ── */
  const handleExport = async () => {
    if (!rows || rows.length === 0) { toast.error('No data to export'); return; }
    const subjectLabel = subjectId
      ? subjects.find(s => s.id.toString() === subjectId)?.code || subjectId
      : 'ALL';
    const filename = `attendance_${department}_${SEM_LABELS[semester]?.replace(/ /g, '_') || semester}_${subjectLabel}_${startDate}_${endDate}.csv`;
    const csv = rowsToCsv(rows, meta);
    const saved = await saveCsv(csv, filename);
    if (saved) toast.success('CSV exported successfully!');
  };

  /* ── group rows by student for display ── */
  const grouped = rows
    ? Object.values(
        rows.reduce((acc, r) => {
          if (!acc[r.usn]) acc[r.usn] = { usn: r.usn, name: r.name, subjects: [] };
          acc[r.usn].subjects.push(r);
          return acc;
        }, {})
      )
    : [];

  /* ══════════════════ render ══════════════════ */
  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header bar ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/attendance/mark')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Export Attendance</h1>
            <p className="text-xs text-gray-500">Generate & download attendance reports as CSV</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">

        {/* ── Filter card ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-800 mb-5 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-slate-900 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
              </svg>
            </span>
            Filter Options
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Date range */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                id="export-start-date"
                type="date"
                value={startDate}
                max={endDate}
                onChange={e => {
                  const newStart = e.target.value;
                  setStartDate(newStart);
                  if (newStart && endDate && diffDays(newStart, endDate) > MAX_RANGE_DAYS) {
                    toast.error('Date range must not exceed 1 year (365 days)');
                  }
                }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                id="export-end-date"
                type="date"
                value={endDate}
                min={startDate}
                max={t}
                onChange={e => {
                  const newEnd = e.target.value;
                  setEndDate(newEnd);
                  if (startDate && newEnd && diffDays(startDate, newEnd) > MAX_RANGE_DAYS) {
                    toast.error('Date range must not exceed 1 year (365 days)');
                  }
                }}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent ${
                  dateRangeExceeded ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
              />
              {dateRangeExceeded && (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Range exceeds 1 year ({rangeDays} days). Max allowed: 365 days.
                </p>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                id="export-department"
                value={department}
                onChange={e => { setDepartment(e.target.value); setSubjectId(''); }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              >
                <option value="">Select Department</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* Semester */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Semester <span className="text-red-500">*</span>
              </label>
              <select
                id="export-semester"
                value={semester}
                onChange={e => { setSemester(e.target.value); setSubjectId(''); }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              >
                <option value="">Select Semester</option>
                {SEMESTERS.map(s => <option key={s} value={s}>{SEM_LABELS[s]}</option>)}
              </select>
            </div>

            {/* Section */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Section <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                id="export-section"
                value={section}
                onChange={e => setSection(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              >
                <option value="">All Sections</option>
                {SECTIONS.map(s => <option key={s} value={s}>Section {s}</option>)}
              </select>
            </div>

            {/* Subject (optional) */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Subject <span className="text-gray-400 font-normal">(optional — blank = all subjects)</span>
              </label>
              <select
                id="export-subject"
                value={subjectId}
                onChange={e => setSubjectId(e.target.value)}
                disabled={loadingSub || !department || !semester}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">All Subjects</option>
                {subjects.map(s => (
                  <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-3 mt-6 pt-5 border-t border-gray-100">
            <button
              id="export-fetch-btn"
              onClick={handleFetch}
              disabled={loading || !department || !semester || dateRangeExceeded}
              className="px-5 py-2.5 bg-slate-100 text-slate-700 border border-slate-300 text-sm font-semibold rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Fetching…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview Data
                </>
              )}
            </button>

            {rows !== null && rows.length > 0 && (
              <button
                id="export-download-btn"
                onClick={handleExport}
                className="px-5 py-2.5 bg-emerald-100 text-emerald-700 border border-emerald-300 text-sm font-semibold rounded-lg hover:bg-emerald-200 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export CSV
              </button>
            )}
          </div>
        </div>

        {/* ── Summary chips ── */}
        {meta && rows !== null && (
          <div className="flex flex-wrap gap-3 mb-5">
            {[
              { label: 'Sessions', value: meta.totalSessions },
              { label: 'Students', value: meta.totalStudents },
              { label: 'Rows', value: rows.length },
              { label: 'Subjects', value: Object.keys(meta.subjects || {}).length },
            ].map(chip => (
              <div key={chip.label} className="bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm flex items-center gap-2">
                <span className="text-xl font-bold text-slate-900">{chip.value}</span>
                <span className="text-xs text-gray-500 font-medium">{chip.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Preview table ── */}
        {rows === null && !loading && (
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-16 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-400 text-sm">Select your filters above and click <strong>Preview Data</strong> to see the attendance report before exporting.</p>
          </div>
        )}

        {rows !== null && rows.length === 0 && (
          <div className="bg-white border border-dashed border-amber-300 rounded-2xl p-16 text-center">
            <svg className="w-12 h-12 text-amber-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-amber-600 text-sm font-medium">No marked attendance records found for the selected filters.</p>
            <p className="text-gray-400 text-xs mt-1">Try a wider date range or different filters.</p>
          </div>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Preview — {rows.length} row{rows.length !== 1 ? 's' : ''}
              </h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-md">Grouped by student</span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">USN</th>
                    <th className="px-4 py-3 text-left font-semibold">Name</th>
                    <th className="px-4 py-3 text-left font-semibold">Subject</th>
                    <th className="px-4 py-3 text-center font-semibold">Total</th>
                    <th className="px-4 py-3 text-center font-semibold text-emerald-600">Present</th>
                    <th className="px-4 py-3 text-center font-semibold text-red-500">Absent</th>
                    <th className="px-4 py-3 text-center font-semibold text-amber-500">Late</th>
                    <th className="px-4 py-3 text-center font-semibold text-blue-500">Excused</th>
                    <th className="px-4 py-3 text-center font-semibold">Attendance&nbsp;%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {grouped.map((student) =>
                    student.subjects.map((r, idx) => (
                      <tr key={`${r.usn}-${r.subjectCode}`} className="hover:bg-gray-50 transition-colors">
                        {idx === 0 && (
                          <>
                            <td className="px-4 py-3 font-mono text-xs text-gray-700 align-top" rowSpan={student.subjects.length}>
                              {r.usn}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900 align-top whitespace-nowrap" rowSpan={student.subjects.length}>
                              {r.name}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-slate-700 font-semibold">{r.subjectCode}</span>
                          <span className="text-gray-400 text-xs ml-1">— {r.subjectName}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{r.totalClasses}</td>
                        <td className="px-4 py-3 text-center font-semibold text-emerald-600">{r.present}</td>
                        <td className="px-4 py-3 text-center font-semibold text-red-500">{r.absent}</td>
                        <td className="px-4 py-3 text-center font-semibold text-amber-500">{r.late}</td>
                        <td className="px-4 py-3 text-center font-semibold text-blue-500">{r.excused}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${badgeColor(r.attendancePercent)}`}>
                            {r.attendancePercent}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer export button */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {rows.length} rows · {meta?.totalStudents} students · {meta?.totalSessions} sessions
              </p>
              <button
                onClick={handleExport}
                className="px-5 py-2 bg-emerald-100 text-emerald-700 border border-emerald-300 text-sm font-semibold rounded-lg hover:bg-emerald-200 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export as CSV
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportAttendance;
